import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Star, Users, Zap, IdCard, Upload, GitMerge } from "lucide-react";
import api from "@/lib/api";
import useCrmConfig from "@/hooks/useCrmConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading, EmptyState } from "@/components/common/Loading";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import QuickCaptureDialog from "@/components/crm/QuickCaptureDialog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "recent", label: "Recent" },
  { key: "follow_up", label: "Follow Up" },
  { key: "favorites", label: "Favorites" },
  { key: "investors", label: "Investors" },
  { key: "founders", label: "Founders" },
  { key: "local", label: "Local" },
];

const Row = ({ contact, onFavorite }) => (
  <li data-testid={`contact-row-${contact.id}`}
    className="group flex items-center gap-3 border-b border-border px-3 py-3 transition-colors last:border-0 hover:bg-secondary/50">
    <button type="button" onClick={() => onFavorite(contact)} aria-pressed={contact.favorite}
      aria-label={contact.favorite ? "Remove favorite" : "Mark as favorite"}
      data-testid={`contact-favorite-${contact.id}`}
      className={`rounded p-1 transition-colors ${contact.favorite
        ? "text-amber-500" : "text-muted-foreground/50 hover:text-amber-500"}`}>
      <Star className={`h-4 w-4 ${contact.favorite ? "fill-amber-400" : ""}`} />
    </button>
    <Link to={`/dashboard/contacts/${contact.id}`} className="min-w-0 flex-1"
      data-testid={`contact-link-${contact.id}`}>
      <span className="block truncate font-[600] text-foreground">{contact.display_name}</span>
      <span className="block truncate text-sm text-muted-foreground">
        {[contact.org?.title, contact.org?.org_name, contact.city].filter(Boolean).join(" · ")
          || contact.how_we_met || "No details yet"}
      </span>
    </Link>
    <div className="hidden shrink-0 flex-wrap justify-end gap-1 sm:flex">
      {(contact.tags || []).slice(0, 3).map((t) => (
        <span key={t.id} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-foreground/70">
          {t.label}
        </span>
      ))}
    </div>
  </li>
);

export default function Contacts() {
  const { visible, loading: configLoading, enums } = useCrmConfig();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState(params.get("q") || "");
  const [dialog, setDialog] = useState(false);
  const [quick, setQuick] = useState(false);
  const [searchError, setSearchError] = useState("");

  const filter = params.get("filter") || "all";

  const load = useCallback(async (query) => {
    try {
      const [list, tally] = await Promise.all([
        api.get("/crm/contacts", { params: { filter, q: query ?? q, limit: 50 } }),
        api.get("/crm/contacts/counts"),
      ]);
      setData(list.data);
      setCounts(tally.data);
      setSearchError("");
    } catch (e) {
      if (e?.response?.status === 400) setSearchError("That search is too long — try fewer words.");
      setData({ contacts: [], total: 0 });
    }
  }, [filter, q]);

  useEffect(() => { if (visible) load(); }, [visible, filter]); // eslint-disable-line

  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const setFilter = (key) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("filter"); else next.set("filter", key);
    setParams(next, { replace: true });
  };

  const toggleFavorite = async (contact) => {
    setData((d) => ({ ...d,
      contacts: d.contacts.map((c) => (c.id === contact.id
        ? { ...c, favorite: !c.favorite } : c)) }));
    try {
      await api.patch(`/crm/contacts/${contact.id}`, { favorite: !contact.favorite });
      load();
    } catch { toast.error("Couldn't update that contact"); load(); }
  };

  const emptyCopy = useMemo(() => {
    if (q) return { title: "No contacts match that search", description: "Try a different name, company or tag." };
    if (filter === "local") {
      return counts.local_city
        ? { title: "No local contacts yet", description: "Contacts with an address in your city will show up here." }
        : { title: "Add your city to use Local", description: "Set your city in Account Settings and give contacts an address." };
    }
    if (filter === "follow_up") {
      return { title: "Nothing due", description: "Set a stay-in-touch cadence on a contact and it will appear here when it's time." };
    }
    return { title: "Your contacts live here", description: "Add the people you meet, then keep the context that makes the relationship useful." };
  }, [q, filter, counts.local_city]);

  if (configLoading) return <Loading />;
  if (!visible) {
    return (
      <div className="py-20 text-center" data-testid="contacts-unavailable">
        <h1 className="text-2xl font-[680]">Page not found</h1>
        <p className="mt-2 text-muted-foreground">This page isn't available on your account.</p>
        <Button className="mt-6" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
      </div>
    );
  }

  return (
    <div data-testid="contacts-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Contacts</h1>
          <p className="mt-1 text-muted-foreground">
            Your private relationship record — only you can see it.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setQuick(true)} data-testid="contacts-quick-capture">
            <Zap className="mr-1.5 h-4 w-4" /> I just met someone
          </Button>
          <Button onClick={() => setDialog(true)} data-testid="contacts-add-button">
            <Plus className="mr-1.5 h-4 w-4" /> Add contact
          </Button>
        </div>
      </div>

      <nav className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm" aria-label="Contact tools">
        <Link to="/dashboard/contacts/my-card" data-testid="contacts-nav-card"
          className="inline-flex items-center gap-1.5 font-[560] text-primary hover:underline">
          <IdCard className="h-4 w-4" /> My contact card
        </Link>
        <Link to="/dashboard/contacts/import" data-testid="contacts-nav-import"
          className="inline-flex items-center gap-1.5 font-[560] text-primary hover:underline">
          <Upload className="h-4 w-4" /> Import &amp; export
        </Link>
        <Link to="/dashboard/contacts/duplicates" data-testid="contacts-nav-duplicates"
          className="inline-flex items-center gap-1.5 font-[560] text-primary hover:underline">
          <GitMerge className="h-4 w-4" /> Duplicates
        </Link>
      </nav>

      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Contact filters">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
              onClick={() => setFilter(f.key)} data-testid={`contacts-filter-${f.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-[560] transition-colors ${
                filter === f.key ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-foreground/70 hover:bg-secondary/80"}`}>
              {f.label}
              {counts[f.key] > 0 && (
                <span className="text-[11px] opacity-70">{counts[f.key]}</span>
              )}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} maxLength={200}
            placeholder="Search name, company, tag…" aria-label="Search contacts"
            data-testid="contacts-search" className="pl-9" />
          {searchError && (
            <p className="mt-1.5 text-xs text-destructive" data-testid="contacts-search-error"
              role="alert">{searchError}</p>
          )}
        </div>
      </div>

      {!data ? <div className="mt-6"><Loading /></div> : data.contacts.length === 0 ? (
        <div className="mt-6">
          <EmptyState icon={Users} testId="contacts-empty" title={emptyCopy.title}
            description={emptyCopy.description}
            action={!q && (
              <Button onClick={() => setDialog(true)} data-testid="contacts-empty-add">
                <Plus className="mr-1.5 h-4 w-4" /> Add your first contact
              </Button>
            )} />
        </div>
      ) : (
        <>
          <ul className="mt-5 overflow-hidden rounded-xl border border-border bg-card"
            data-testid="contacts-list">
            {data.contacts.map((c) => (
              <Row key={c.id} contact={c} onFavorite={toggleFavorite} />
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="contacts-total">
            Showing {data.contacts.length} of {data.total}
          </p>
        </>
      )}

      <ContactFormDialog open={dialog} onOpenChange={setDialog} enums={enums}
        onSaved={() => load()} />
      <QuickCaptureDialog open={quick} onOpenChange={setQuick} onSaved={() => load()} />
    </div>
  );
}
